import { Link } from "react-router-dom";
import { Heart, Target, BookOpen, Image, Mail, Users, MessageSquare, CheckSquare, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: "easeOut" as const },
  }),
};

function HeroIllustration() {
  return (
    <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm mx-auto">
      {/* Sage circle */}
      <circle cx="160" cy="200" r="120" fill="hsl(var(--sage))" fillOpacity="0.18" />
      {/* Terracotta circle overlapping */}
      <circle cx="240" cy="200" r="120" fill="hsl(var(--accent))" fillOpacity="0.16" />
      {/* Overlap blend */}
      <ellipse cx="200" cy="200" rx="60" ry="100" fill="hsl(var(--sage))" fillOpacity="0.10" />

      {/* Floating icons */}
      {/* Heart */}
      <g transform="translate(120, 100)">
        <circle r="18" cx="18" cy="18" fill="hsl(var(--accent))" fillOpacity="0.22" />
        <text x="10" y="24" fontSize="18" fill="hsl(var(--accent))">♥</text>
      </g>
      {/* Target */}
      <g transform="translate(280, 120)">
        <circle r="16" cx="16" cy="16" fill="hsl(var(--sage))" fillOpacity="0.25" />
        <circle cx="16" cy="16" r="6" stroke="hsl(var(--sage))" strokeWidth="2" fill="none" />
        <circle cx="16" cy="16" r="2" fill="hsl(var(--sage))" />
      </g>
      {/* Book */}
      <g transform="translate(170, 310)">
        <circle r="16" cx="16" cy="16" fill="hsl(var(--accent))" fillOpacity="0.18" />
        <rect x="8" y="10" width="16" height="12" rx="1" stroke="hsl(var(--accent))" strokeWidth="1.5" fill="none" />
        <line x1="16" y1="10" x2="16" y2="22" stroke="hsl(var(--accent))" strokeWidth="1" />
      </g>
      {/* Star / sparkle */}
      <g transform="translate(260, 290)">
        <circle r="14" cx="14" cy="14" fill="hsl(var(--sage))" fillOpacity="0.20" />
        <text x="6" y="20" fontSize="16" fill="hsl(var(--sage))">✦</text>
      </g>
      {/* Small dot accents */}
      <circle cx="100" cy="260" r="4" fill="hsl(var(--accent))" fillOpacity="0.3" />
      <circle cx="310" cy="180" r="5" fill="hsl(var(--sage))" fillOpacity="0.3" />
      <circle cx="200" cy="90" r="3" fill="hsl(var(--accent))" fillOpacity="0.25" />
    </svg>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-accent" fill="currentColor" />
            <span className="font-display text-xl font-semibold tracking-tight">Together OS</span>
          </div>
          <Link
            to="/app"
            className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Get started
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative max-w-5xl mx-auto px-4 md:px-8 pt-20 md:pt-32 pb-16 md:pb-24">
        {/* Decorative shapes */}
        <div className="absolute top-12 right-0 w-64 h-64 rounded-full bg-sage/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-8 w-48 h-48 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-12 md:gap-16">
          {/* Text */}
          <motion.div
            initial="hidden"
            animate="visible"
            className="flex-1 max-w-xl"
          >
            <motion.h1
              custom={0}
              variants={fadeUp}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.15] tracking-tight"
            >
              Set goals together. Reflect often. Grow as a couple.
            </motion.h1>
            <motion.p
              custom={1}
              variants={fadeUp}
              className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed"
            >
              A private, journal-first workspace where couples align on what matters, track progress gently, and strengthen their partnership through structured reflection.
            </motion.p>
            <motion.div custom={2} variants={fadeUp} className="mt-8">
              <Link
                to="/app"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-primary-foreground rounded-xl text-base font-medium hover:bg-primary/90 transition-colors shadow-sm"
              >
                Get started free
              </Link>
            </motion.div>
          </motion.div>

          {/* Illustration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="flex-1 w-full max-w-sm md:max-w-md"
          >
            <HeroIllustration />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="font-display text-2xl md:text-3xl font-semibold text-center mb-12"
        >
          Everything you need, nothing you don't
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Target,
              title: "Shared Goals",
              desc: "Set meaningful goals across finances, health, relationship, and personal growth. Track progress gently — no dashboards or streaks.",
              bg: "bg-sage-light",
            },
            {
              icon: BookOpen,
              title: "Board Meetings",
              desc: "A structured reflection ritual you can do weekly, monthly, or whenever you need it. Reflect on what went well, what didn't, and what could be better.",
              bg: "bg-terracotta-light",
            },
            {
              icon: Image,
              title: "Vision Board & Memories",
              desc: "A canvas for your dreams plus a shared photo gallery to celebrate your journey together.",
              bg: "bg-secondary",
            },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`${f.bg} border border-border rounded-xl p-8 space-y-4`}
            >
              <f.icon className="w-6 h-6 text-foreground" />
              <h3 className="font-display text-xl font-semibold">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-card border-y border-border">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-display text-2xl md:text-3xl font-semibold text-center mb-12"
          >
            How it works
          </motion.h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { step: "1", icon: Mail, title: "Sign up with your email", desc: "Create your account in seconds — no passwords, just a magic link." },
              { step: "2", icon: Users, title: "Invite your partner", desc: "Send an email invite or share a link code. Your partner joins your space instantly." },
              { step: "3", icon: Target, title: "Set your first goal together", desc: "Pick a category — finances, health, relationship, or personal — and define what matters most." },
              { step: "4", icon: Sparkles, title: "Add memories and vision items", desc: "Upload photos, write affirmations, and build a shared vision board that evolves with you." },
              { step: "5", icon: MessageSquare, title: "Hold your first board meeting", desc: "Reflect on what went well, what didn't, and what could be better. Together." },
            ].map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="text-center space-y-4"
              >
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto font-display text-xl font-semibold">
                  {s.step}
                </div>
                <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 py-20 md:py-28 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <h2 className="font-display text-2xl md:text-3xl font-semibold">
            Ready to grow together?
          </h2>
          <Link
            to="/app"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-primary-foreground rounded-xl text-base font-medium hover:bg-primary/90 transition-colors shadow-sm"
          >
            Get started free
          </Link>
          <p className="text-sm text-muted-foreground">No credit card required.</p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-accent" fill="currentColor" />
            <span className="font-display text-sm font-semibold">Together OS</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Your data is private. Only you and your partner can see it.
          </p>
        </div>
      </footer>
    </div>
  );
}
